import { defineStore } from 'pinia';
import type { SrtEntry, SubtitleState, HistoryChange, WordEntry } from '@lambda-libs/types';
import { shouldSplit } from '@utils/shouldSplit';
import { notify } from '../components/NotificationPlugin';
import { useLogStore } from './logStore';
import { getTranslation } from '../i18nlocal';
import { flushPendingSubtitleUpdates, hasPendingSubtitleUpdates } from '../components/SrtEditor/UpdateWords';

// Collects validation reason strings when validateEntry runs in quiet mode
// (batch paths like addEntries aggregate these into a single notification).
let quietValidationReasons: string[] = [];

// Validation functions
const validateWordTimings = (entry: SrtEntry): string[] => {
  const errors: string[] = [];
  
  if (!entry.words || entry.words.length === 0) {
    return errors;
  }
  
  // Check if words are sorted by start time
  for (let i = 0; i < entry.words.length - 1; i++) {
    const current = entry.words[i];
    const next = entry.words[i + 1];
    
    // Check for overlapping words
    if (current.end > next.start) {
      errors.push(`Word overlap in entry ${entry.id}: "${current.word}" (${current.start}-${current.end}) overlaps with "${next.word}" (${next.start}-${next.end})`);
    }
    
    // Check for invalid word timing (allow newlines to have same start/end time)
    if (current.start >= current.end && current.word !== '\n' && current.word !== '') {
      errors.push(`Invalid word timing in entry ${entry.id}: "${current.word}" start time (${current.start}) >= end time (${current.end})`);
    }
  }
  
  // Check last word timing (allow newlines to have same start/end time)
  const lastWord = entry.words[entry.words.length - 1];
  if (lastWord.start >= lastWord.end && lastWord.word !== '\n' && lastWord.word !== '') {
    errors.push(`Invalid word timing in entry ${entry.id}: "${lastWord.word}" start time (${lastWord.start}) >= end time (${lastWord.end})`);
  }
  
  // Check if words are within entry bounds
  const firstWord = entry.words[0];
  if (firstWord.start < entry.startTime - 0.01) { // Allow small tolerance
    errors.push(`Word starts before entry in ${entry.id}: "${firstWord.word}" starts at ${firstWord.start}, entry starts at ${entry.startTime}`);
  }
  
  if (lastWord.end > entry.endTime + 0.01) { // Allow small tolerance
    errors.push(`Word ends after entry in ${entry.id}: "${lastWord.word}" ends at ${lastWord.end}, entry ends at ${entry.endTime}`);
  }
  
  return errors;
};

const validateBlockTimings = (entries: SrtEntry[]): string[] => {
  const errors: string[] = [];
  
  // Sort entries by start time for validation
  const sortedEntries = [...entries].sort((a, b) => a.startTime - b.startTime);
  
  for (let i = 0; i < sortedEntries.length; i++) {
    const current = sortedEntries[i];
    
    // Check for invalid block timing
    if (current.startTime >= current.endTime) {
      errors.push(`Invalid block timing: Entry ${current.id} start time (${current.startTime}) >= end time (${current.endTime})`);
    }
    
    // Check for negative timing
    if (current.startTime < 0) {
      errors.push(`Negative start time: Entry ${current.id} starts at ${current.startTime}`);
    }
    
    // Check for overlapping blocks
    if (i < sortedEntries.length - 1) {
      const next = sortedEntries[i + 1];
      if (current.endTime > next.startTime + 0.01) { // Allow small tolerance
        errors.push(`Block overlap: Entry ${current.id} (${current.startTime}-${current.endTime}) overlaps with entry ${next.id} (${next.startTime}-${next.endTime})`);
      }
    }
  }
  
  return errors;
};

const validateAllTimings = (entries: SrtEntry[]): void => {
  const blockErrors = validateBlockTimings(entries);
  const wordErrors: string[] = [];
  
  // Check word timings for each entry
  entries.forEach(entry => {
    const entryWordErrors = validateWordTimings(entry);
    wordErrors.push(...entryWordErrors);
  });
  
  // Log all errors
  if (blockErrors.length > 0) {
    console.error('Block timing errors:', blockErrors);
  }
  
  if (wordErrors.length > 0) {
    console.error('Word timing errors:', wordErrors);
  }
  
  // Show notification if there are errors
  if (blockErrors.length > 0 || wordErrors.length > 0) {
    //notify('danger', `Validation errors found: ${blockErrors.length} block errors, ${wordErrors.length} word errors. Check console for details.`);
  }
};

let changeListeners: Array<() => void> = [];

// Add this function to register change listeners
export function registerChangeListener(listener: () => void) {
  changeListeners.push(listener);
  return () => {
    changeListeners = changeListeners.filter(l => l !== listener);
  };
}

// Add this function to notify all registered listeners of changes
const notifyChanges = () => {
  changeListeners.forEach(listener => listener());
};


const DB_NAME = 'SubtitleEditorDB';
const STORE_NAME = 'history';
const DB_VERSION = 4; // Increased for new schema
const MAX_HISTORY_SIZE = 50;
const BATCH_TIMEOUT = 500;
let split_index = 0;
// Add connection pooling
let dbConnection: IDBDatabase | null = null;
let connectionPromise: Promise<IDBDatabase> | null = null;
let pendingHistoryChange: Promise<void> | null = null;

// Explicit failure for source-adoption operations (transcript replacement,
// cache clear). 'invalid' = bad input, 'conflict' = the outgoing document was
// modified after the pre-save snapshot (or a commit window is open), so the
// operation aborted rather than silently discarding edits. 'unavailable' = the
// IndexedDB transaction failed; in-memory state was preserved. Callers must
// catch this (loadSrtData already returns false on throw; the sign-out route
// must add a .catch — see the wave report).
export class TranscriptAdoptionError extends Error {
  constructor(public code: 'conflict' | 'unavailable' | 'invalid', message: string) {
    super(message);
    this.name = 'TranscriptAdoptionError';
  }
}

// Serializes IndexedDB history work per store instance: every flush and every
// source replacement chains onto the previous run, so two batches can never
// interleave their snapshot/commit steps. Kept outside SubtitleState (which
// lives in shared libs) via instance-keyed maps.
const flushChains = new WeakMap<object, Promise<void>>();

// Bumped by addToHistory, the single funnel for every history-recorded
// mutation (add/update/delete/split/merge/bulk). Source adoption snapshots
// this before touching IndexedDB: a different value afterwards proves the
// outgoing document was modified mid-operation (a conflict), even when the
// array identity/length happens to look unchanged.
const documentRevisions = new WeakMap<object, number>();
const revisionOf = (store: object): number => documentRevisions.get(store) ?? 0;
const bumpRevision = (store: object): void => {
  documentRevisions.set(store, revisionOf(store) + 1);
};

// Held while a replacement/clear transaction is committing (the only window
// in which the check-then-adopt sequence cannot re-validate). Mutations that
// arrive inside the window are rejected explicitly at the boundary —
// updateEntry returns false, addToHistory throws TranscriptAdoptionError —
// never applied-then-wiped by the adoption patch.
const adoptionLocks = new WeakMap<object, boolean>();
const isAdopting = (store: object): boolean => adoptionLocks.get(store) === true;

// IndexedDB keys for history rows come from timestamps. Two flushes inside
// one millisecond used to collide on the keyPath and fail the second add
// with ConstraintError; keys are now strictly monotonic per module instance
// (the IDB store is shared across documents, so the floor is module-level).
let lastHistoryTimestamp = 0;
const nextHistoryTimestamp = (): number => {
  const now = Date.now();
  lastHistoryTimestamp = Math.max(now, lastHistoryTimestamp + 1);
  return lastHistoryTimestamp;
};
// Bumped whenever the current document generation is discarded
// (source replacement, cache clear). A flush snapshot committed under an
// older generation belongs to a discarded document and must be dropped,
// never written into the new source's history.
const historyGenerations = new WeakMap<object, number>();
const generationOf = (store: object): number => historyGenerations.get(store) ?? 0;

// Runtime source tag for a queued history change. HistoryChange itself is a
// shared-libs type, so the tag travels as an extra runtime property written
// only by addToHistory. Entries without a tag predate it and are treated as
// belonging to the snapshotting source.
const historySourceOf = (change: HistoryChange): string | null => {
  const tag = (change as HistoryChange & { historySourceId?: unknown }).historySourceId;
  return typeof tag === 'string' ? tag : null;
};


export const useSubtitleStore = defineStore('subtitle', {
  state: (): SubtitleState => ({
    sourceId: '',
    srtData: [],
    enforceRules: true,
    hasSeenAll: false,
    maxCPL: 42,
    maxCPS: 15,
    maxLines: 3,
    autoSave: false,
    autoSaveInterval: 0,
    srtDuration: 0,
    selectedEntriesisAutomatic: false,
    selectedEntries: [],
    historyIndex: -1,
    pendingChanges: [],
    batchTimeoutId: null,
    totalHistoryEntries: 0,
    lastBatchSize: 0,
    isDbBusy: false,
    interfaceNeedsRefresh: 0,
    pendingFlush: null,
    currentBatchSize: 0,
    lastKnownDbSize: 0,
    fontSize: 12,
  }),


  actions: {
    markPendingEdit() { notifyChanges(); },
    // Bumps the adoption revision counter for mutation paths that do not
    // record history (undo internals, bulk fix-ups that replace srtData
    // directly). Source adoption snapshots this counter before touching
    // IndexedDB: any bump before the commit aborts with an explicit
    // `conflict` instead of silently replacing the edited document.
    // Selection, refresh counters, and validation normalization are not
    // document mutations and must not call this (they would force spurious
    // conflicts on every load raced by a click).
    touchRevision() { bumpRevision(this); },
      async initDB(): Promise<IDBDatabase> {
      
        // If we already have a connection, return it
        if (dbConnection) {
          return dbConnection;
        }
        // If we're already connecting, return the promise
        if (connectionPromise) {
          return connectionPromise;
        }
  
        // Create new connection
        connectionPromise = new Promise((resolve, reject) => {
          const request = indexedDB.open(DB_NAME, DB_VERSION);
  
          request.onerror = () => {
            connectionPromise = null;
            reject(request.error);
          };
  
          request.onsuccess = () => {
            dbConnection = request.result;
            
            // Handle connection loss
            dbConnection.onclose = () => {
              dbConnection = null;
              connectionPromise = null;
            };
  
            
            dbConnection.onerror = () => {
              dbConnection = null;
              connectionPromise = null;
            };
  
            resolve(dbConnection);
          };
  
          request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              const store = db.createObjectStore(STORE_NAME, { keyPath: 'timestamp' });
              store.createIndex('timestamp_idx', 'timestamp');
            } else {
              const store = request.transaction?.objectStore(STORE_NAME);
              if (store && !store.indexNames.contains('timestamp_idx')) {
                store.createIndex('timestamp_idx', 'timestamp');
              }
            }
          };
        });
  
        return connectionPromise;
      },
    createHistoryChange(
      type: HistoryChange['type'],
      beforeEntries: Partial<SrtEntry>[],
      afterEntries: Partial<SrtEntry>[]
    ): HistoryChange {
      return {
        type,
        // Strictly monotonic (see nextHistoryTimestamp): two records created
        // in the same millisecond must never share a keyPath value, or the
        // second IndexedDB add fails with ConstraintError.
        timestamp: nextHistoryTimestamp(),
        affected: {
          before: beforeEntries,
          after: afterEntries,
        },
      };
    },

     // Helper method to convert proxy objects to plain objects
     toPlainObject<T>(obj: T): T {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => this.toPlainObject(item)) as unknown as T;
      }

      const plainObj = {} as T;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          plainObj[key] = this.toPlainObject(obj[key]);
        }
      }
      return plainObj;
    },

     // Modified history-related methods to handle proxy objects.
     // Returns the queued entry so callers can retract it (see updateEntry).
     addToHistory(change: HistoryChange): HistoryChange {
      // A replacement/clear commit is in flight: recording here would let the
      // adoption patch wipe the entry silently. Fail explicitly instead so
      // the racing action (add/split/merge/…) surfaces instead of vanishing.
      if (isAdopting(this)) {
        throw new TranscriptAdoptionError('conflict', 'Transcript changed during source adoption; retry after the load settles');
      }
      if (this.batchTimeoutId) {
        clearTimeout(this.batchTimeoutId);
        this.batchTimeoutId = null;
      }
      bumpRevision(this);

      // Convert the change object to a plain object before adding to pending changes
      const plainChange = this.toPlainObject(change);
      // Tag the queued entry with the source it was recorded against. Flush
      // snapshots use this tag to keep one source's history out of another's.
      (plainChange as HistoryChange & { historySourceId: string }).historySourceId = this.sourceId;
      this.pendingChanges.push(plainChange);

      const pendingTimeout = setTimeout(() => {
        // An obsolete timer (source replaced or cache cleared after it was
        // armed) must not flush a newer document's queue from stale context.
        if (this.batchTimeoutId !== pendingTimeout) return;
        this.batchTimeoutId = null;
        void this.flushHistoryBatch().catch((error) => {
          console.error('Error during scheduled history flush:', error);
        });
      }, BATCH_TIMEOUT);
      this.batchTimeoutId = pendingTimeout;
      return plainChange;
    },

    // True while any editor work is not yet durably recorded: queued text
    // edits, unsent history batches, or an in-flight history transaction.
    // Used by the beforeunload guard so the browser only prompts when there
    // is actually something to lose.
    hasUnsavedWork(): boolean {
      if (this.pendingChanges.length > 0 || this.batchTimeoutId !== null || this.pendingFlush !== null) return true;
      try {
        return hasPendingSubtitleUpdates(this);
      } catch {
        return false;
      }
    },

    applyEditorChange(sourceId: string, before: SrtEntry[], after: SrtEntry[], selectedIds: string[]) {
      if (this.sourceId !== sourceId || JSON.stringify(this.srtData) !== JSON.stringify(before)) throw new Error('Transcript changed before applying edit');
      // History insertion and the in-memory replacement happen in one call
      // stack, so another edit/source load cannot land between them.
      // addToHistory already bumps the adoption revision; the explicit bump
      // below keeps that invariant visible at this entry point (a racing
      // replaceTranscript must see this edit and abort with `conflict`).
      this.addToHistory(this.createHistoryChange('bulk', before, after));
      bumpRevision(this);
      this.srtData = after;
      this.selectedEntries = selectedIds;
      this.interfaceNeedsRefresh++;
      notifyChanges();
    },

    

    // Serialized entry point: chains onto the previous history run so two
    // flushes (timer, explicit, unload) can never interleave. EVERY caller
    // joins the chain — even with an empty queue — so awaiting this also
    // awaits an in-flight flush, replacement, or clear instead of resolving
    // early while another run still owns the database. The caller observes
    // its own run; a failure propagates to the caller but never breaks the
    // chain for later work.
    async flushHistoryBatch() {
      // Captured when queued, not when the run starts: anything that replaces
      // the document while this call waits on the chain must be detected
      // before the run touches source-specific counters.
      const queued = { generation: generationOf(this), sourceId: this.sourceId };
      const previous = flushChains.get(this) ?? Promise.resolve();
      const run = previous.then(() => this.flushHistoryBatchInner(queued));
      // The chain itself must survive a failure; only the caller sees it.
      flushChains.set(this, run.catch(() => undefined));
      this.pendingFlush = run;
      try {
        await run;
      } finally {
        if (this.pendingFlush === run) this.pendingFlush = null;
      }
    },

    // One serialized history run. Snapshots the live queue (source-aware)
    // before touching IndexedDB, commits inside a single transaction, and
    // acknowledges only on transaction commit. The run drains in a loop so
    // edits arriving mid-flush are persisted by the same caller instead of
    // waiting for the next timer; a failure keeps the snapshot in place, in
    // order, without duplicating anything.
    async flushHistoryBatchInner(queued?: { generation: number; sourceId: string }) {
      if (this.batchTimeoutId) {
        clearTimeout(this.batchTimeoutId);
        this.batchTimeoutId = null;
      }
      const generation = generationOf(this);
      const sourceId = this.sourceId;
      this.isDbBusy = true;
      try {
        for (;;) {
          if (this.pendingChanges.length === 0) {
            // Queue drained: disarm any batch timer armed by edits this run
            // already persisted, so hasUnsavedWork() reports clean and no
            // stray flush fires after the window.
            if (this.batchTimeoutId) { clearTimeout(this.batchTimeoutId); this.batchTimeoutId = null; }
            return;
          }
          // Keep only this document's entries; strays tagged with a discarded
          // source (a late old-document edit landing after a replacement) are
          // dropped here so they can never bleed into the new source's history.
          const snapshot = this.pendingChanges.filter((change) => {
            const tag = historySourceOf(change);
            return tag === null || tag === sourceId;
          });
          const strays = this.pendingChanges.length - snapshot.length;
          if (strays > 0) {
            // Keep only this document's snapshot; the strays are dropped.
            this.pendingChanges = snapshot;
            console.warn(`Dropping ${strays} history change(s) from a replaced source`);
          }
          if (snapshot.length === 0) {
            if (this.batchTimeoutId) { clearTimeout(this.batchTimeoutId); this.batchTimeoutId = null; }
            return;
          }
          const snapshotSet = new Set(snapshot);
          const db = await this.initDB();
          // A source replacement or cache clear while queued or while the
          // database was opening discards this document generation: drop the
          // snapshot without writing it into the new source's history.
          if (
            generationOf(this) !== generation || this.sourceId !== sourceId ||
            (queued && (queued.generation !== generation || queued.sourceId !== sourceId))
          ) {
            this.removeFlushedChanges(snapshotSet);
            return;
          }
          const batchedChange = this.toPlainObject(this.createBatchedChange(snapshot));
          this.currentBatchSize = snapshot.length;
          // Observed inside the committing transaction: the only honest basis
          // for the in-memory counters. historyIndex used to increment blindly
          // per batch while pruning deleted rows, so it drifted past MAX and
          // past the real row count; it is now recomputed from transaction
          // truth after every commit.
          const observed = { countBefore: 0, pruned: 0 };
          await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            // Acknowledge only when the whole transaction commits: request
            // success alone can precede a later abort.
            tx.oncomplete = () => resolve();
            tx.onabort = () => reject(tx.error || new Error('History batch aborted'));
            tx.onerror = () => reject(tx.error || new Error('History batch failed'));
            const fail = () => {
              try { tx.abort(); } catch { /* onabort reports the failure */ }
            };
            const countRequest = store.count();
            countRequest.onerror = fail;
            countRequest.onsuccess = (event) => {
              observed.countBefore = (event.target as IDBRequest<number>).result;
              let addRequest: IDBRequest<IDBValidKey>;
              try {
                addRequest = store.add(batchedChange);
              } catch {
                fail();
                return;
              }
              addRequest.onerror = fail;
              addRequest.onsuccess = () => {
                // Bound the history inside the same transaction: issuing the
                // follow-up requests from the event callback keeps them on the
                // live transaction (awaiting between requests may let it
                // auto-commit and throw TransactionInactiveError).
                try {
                  this.cleanupOldEntries(store, fail, observed);
                } catch {
                  fail();
                }
              };
            };
          });
          // The transaction committed: exactly the snapshotted entries leave
          // the live queue, in order. The loop then picks up anything that
          // arrived mid-flush.
          this.removeFlushedChanges(snapshotSet);
          if (generationOf(this) !== generation || this.sourceId !== sourceId) {
            // Defensive only: chain members that bump the generation cannot
            // run during this run, so reaching here means the discipline was
            // bypassed (e.g. a direct inner call racing a replacement). Still
            // reconcile the counters from transaction truth rather than
            // incrementing blindly into the new document's cursor.
            console.warn('History batch committed across a source change; reconciling counters from transaction');
          }
          const actualSize = observed.countBefore + 1 - observed.pruned;
          this.historyIndex = actualSize - 1;
          this.lastKnownDbSize = actualSize;
          this.totalHistoryEntries = actualSize;
        }
      } catch (error) {
        // The snapshot was never removed: a retry persists exactly these
        // entries, in their original order, with no duplication.
        console.error('Error during history flush:', error);
        throw error;
      } finally {
        this.isDbBusy = false;
      }
    },

    // Removes exactly the committed snapshot entries (by identity), keeping
    // any edits that arrived during the flush in their original order.
    removeFlushedChanges(snapshotSet: Set<HistoryChange>) {
      if (snapshotSet.size === 0) return;
      this.pendingChanges = this.pendingChanges.filter((change) => !snapshotSet.has(change));
    },

    async waitForPendingOperations() {
      if (this.batchTimeoutId) {
        clearTimeout(this.batchTimeoutId);
        this.batchTimeoutId = null;
      }
      await this.flushHistoryBatch();
      if (this.pendingFlush) {
        await this.pendingFlush;
      }
    },

    async undo() {
      await flushPendingSubtitleUpdates(this);
      //console.log('undoing last change called');
      // Wait for any pending operations (this already drains the batch timer
      // and the serialized flush; no second flush needed).
      await this.waitForPendingOperations();

      if (this.historyIndex <= 0) {
        return;
      }


      this.isDbBusy = true;
      try {
        const db = await this.initDB();
        const historyEntries = await this.getHistoryEntries(db);
        
        if (this.historyIndex >= historyEntries.length) {
          this.historyIndex = historyEntries.length - 1;
        }

        const currentChange = historyEntries[this.historyIndex];
        if (!currentChange) {
          console.error('Invalid history state');
          throw new Error('Invalid history state');
        }
       

        // Apply the undo
        await this.applyHistoryChange(currentChange, true);
        
        // Remove the last change from the history in the database, acknowledged
        // on transaction commit rather than request success.
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          tx.oncomplete = () => resolve();
          tx.onabort = () => reject(tx.error || new Error('Undo history removal aborted'));
          tx.onerror = () => reject(tx.error || new Error('Undo history removal failed'));
          const store = tx.objectStore(STORE_NAME);
          const deleteRequest = store.delete(currentChange.timestamp);
          deleteRequest.onerror = () => {
            try { tx.abort(); } catch { /* onabort reports the failure */ }
          };
        });
        
        // Update indices from the delete that just committed: the row is gone,
        // so the size is one less than the pre-delete fetch (recording the
        // pre-delete length here used to drift the counters above reality).
        this.historyIndex--;
        this.lastKnownDbSize = historyEntries.length - 1;
        this.totalHistoryEntries = this.lastKnownDbSize;

      } catch (error) {
        console.error('Error during undo:', error);
        throw error;
      } finally {
        this.isDbBusy = false;
      }
    },

    // Helper method to get history entries
    async getHistoryEntries(db: IDBDatabase): Promise<HistoryChange[]> {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      if (store)
      {
        const index = store.index('timestamp_idx');

        return new Promise<HistoryChange[]>((resolve, reject) => {
          const entries: HistoryChange[] = [];
          const cursorRequest = index.openCursor();

          cursorRequest.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
              entries.push(cursor.value as HistoryChange);
              cursor.continue();
            } else {
              resolve(entries.sort((a, b) => a.timestamp - b.timestamp));
            }
          };
          cursorRequest.onerror = () => reject(cursorRequest.error);
        });
      }
      else
      {
        console.warn('History object store not found');
      }
      return [];
    },

    createBatchedChange(input?: HistoryChange[]): HistoryChange {
      // `this` is untyped in parameter defaults under Pinia options stores;
      // resolve the default inside the body (same behavior).
      const changes = input ?? this.pendingChanges;
      const batchedChange: HistoryChange = {
        type: 'bulk',
        // Monotonic: the batched row's keyPath value must be unique even when
        // two flushes commit within one millisecond.
        timestamp: nextHistoryTimestamp(),
        affected: {
          before: [],
          after: []
        }
      };
    
      // Track entries by ID for both before and after states
      const beforeEntries = new Map<string, any>();
      const afterEntries = new Map<string, any>();
      // IDs whose first appearance in the batch is in an `after` array were
      // created by a batched operation (for example, split halves or a merged
      // entry). They must not leak into the batched `before` state, otherwise
      // undo restores both the pre-operation original and its derived entry,
      // producing overlapping/double captions.
      const batchCreatedIds = new Set<string>();
    
      // Process changes in order
      for (const change of changes) {
        // Track first 'before' state for each entry, but only for IDs that
        // already existed before this batch began.
        for (const entry of change.affected.before) {
          const plainEntry = this.toPlainObject(entry);
          if (plainEntry.id === undefined) plainEntry.id = 'unknown';
          if (!batchCreatedIds.has(plainEntry.id) && !afterEntries.has(plainEntry.id) && !beforeEntries.has(plainEntry.id)) {
            beforeEntries.set(plainEntry.id, plainEntry);
          }
        }
    
        // Update 'after' state for each entry (will keep overwriting with latest)
        for (const entry of change.affected.after) {
          const plainEntry = this.toPlainObject(entry);
          if (plainEntry.id === undefined) plainEntry.id = 'unknown';
          if (!beforeEntries.has(plainEntry.id) && !afterEntries.has(plainEntry.id)) {
            batchCreatedIds.add(plainEntry.id);
          }
          afterEntries.set(plainEntry.id, plainEntry);
        }
      }
    
      // Convert maps to arrays for final output
      batchedChange.affected.before = Array.from(beforeEntries.values());
      batchedChange.affected.after = Array.from(afterEntries.values());
    
      return batchedChange;
    },

    // Bounds the persisted history to MAX_HISTORY_SIZE, oldest first. Must be
    // called from a live transaction's request callback (see the flush): every
    // follow-up request is issued synchronously from a request callback so it
    // stays on the same transaction. Failures are reported through onFail so
    // the owning transaction aborts instead of partially committing.
    // Pruned rows are counted into `observed` — never into the store counters
    // here: the flush reconciles historyIndex/lastKnownDbSize/totalHistoryEntries
    // once, from transaction truth, after the commit lands.
    cleanupOldEntries(store: IDBObjectStore, onFail: () => void, observed: { pruned: number }) {
      const countRequest = store.count();
      countRequest.onerror = onFail;
      countRequest.onsuccess = (event) => {
        const count = (event.target as IDBRequest<number>).result;
        if (count <= MAX_HISTORY_SIZE) return;
        let remaining = count - MAX_HISTORY_SIZE;
        // The store is keyed by timestamp, so a key range built from the
        // in-memory history position could never match (and the history grew
        // without bound). Walk the timestamp index oldest-first instead.
        let index: IDBIndex;
        try {
          index = store.index('timestamp_idx');
        } catch {
          onFail();
          return;
        }
        const cursorRequest = index.openCursor();
        cursorRequest.onerror = onFail;
        cursorRequest.onsuccess = (cursorEvent) => {
          const cursor = (cursorEvent.target as IDBRequest<IDBCursorWithValue | null>).result;
          if (!cursor || remaining <= 0) return;
          remaining--;
          let deleteRequest: IDBRequest<undefined>;
          try {
            deleteRequest = store.delete(cursor.primaryKey);
          } catch {
            onFail();
            return;
          }
          deleteRequest.onerror = onFail;
          deleteRequest.onsuccess = () => {
            observed.pruned++;
            cursor.continue();
          };
        };
      };
    },

    async applyHistoryChange(change: HistoryChange, isUndo: boolean) {
      // Wait for any pending operations
      if (pendingHistoryChange) {
        await pendingHistoryChange;
      }
    
      try {
        // Create a new promise for this operation
        pendingHistoryChange = (async () => {
          const beforeEntries = isUndo ? change.affected.after : change.affected.before;
          const afterEntries = isUndo ? change.affected.before : change.affected.after;
    
          // Remove entries
          for (const entry of beforeEntries) {
            const index = this.srtData.findIndex(e => e.id === entry.id);
            if (index !== -1) {
              this.srtData.splice(index, 1);
            } else {
              // Undo internals must never toast per entry — log only
              console.warn('Entry not found:', entry.id);
              useLogStore().addMessage({ message: 'Entry not found: ' + entry.id, type: 'warning' });
            }
          }
    
          // Add new entries
          for (const entry of afterEntries) {
            const plainEntry = this.toPlainObject(entry);
            this.srtData.push(plainEntry as SrtEntry);
          }
    
          // Sort entries
          this.srtData.sort((a, b) => a.startTime - b.startTime);
          // Undo/redo rewrite rows without recording history: bump the
          // adoption revision explicitly so a racing source replacement
          // aborts with `conflict` instead of wiping the restored rows.
          bumpRevision(this);
          this.interfaceNeedsRefresh++;
          notifyChanges();
        })();
    
        // Wait for the operation to complete
        await pendingHistoryChange;
      } catch (error) {
        console.error('Error applying history change:', error);
        notify('danger', 'Failed to apply history change');
      } finally {
        // Clear the pending operation if this was the last one
        if (pendingHistoryChange === pendingHistoryChange) {
          pendingHistoryChange = null;
        }
      }
    },

    async splitEntry(id: string, textArea: any, enforceRules: boolean) {
      const cursorPosition = textArea.selectionStart;
      this.splitBlock(id, cursorPosition, enforceRules);
    },

    async splitBlock(id: string, cursorPosition: number, enforceRules: boolean) {
      const entry = this.srtData.find(entry => entry.id === id);
      
      if (!entry) return;
      
      const insertIndex = this.srtData.findIndex(entry => entry.id === id);
      const text = entry.text || '';
      const totalLength = text.length;
      const duration = entry.endTime - entry.startTime;
      
      // Find the nearest valid split point
      let splitIndex = cursorPosition;
      
      // Track if we're splitting within a word
      let isInWord = false;
      let splitWordIndex = -1;
      
      if (entry.words) {
        // Keep track of word positions and their indices
        const wordPositions: Array<{word: string, start: number, index: number}> = [];
        let currentPosition = 0;
        
        // Build an array of word positions with their original indices
        for (let i = 0; i < entry.words.length; i++) {
          const word = entry.words[i];
          const wordStart = text.indexOf(word.word, currentPosition);
          if (wordStart !== -1) {
            wordPositions.push({
              word: word.word,
              start: wordStart,
              index: i
            });
            currentPosition = wordStart + word.word.length;
          }
        }
        
        // Find which word contains the cursor position
        for (const wordPos of wordPositions) {
          const wordEnd = wordPos.start + wordPos.word.length;
          if (cursorPosition > wordPos.start && cursorPosition < wordEnd) {
            isInWord = true;
            splitWordIndex = wordPos.index;
            break;
          }
        }
      }
      
      if (enforceRules === true) {
        // Original logic for finding split point
        while (splitIndex > 0 && !shouldSplit(text, splitIndex, enforceRules)) {
          splitIndex--;
        }
        
        if (splitIndex === 0) {
          splitIndex = cursorPosition;
          while (splitIndex < totalLength && !shouldSplit(text, splitIndex, enforceRules)) {
            splitIndex++;
          }
        }
        
        if (splitIndex === totalLength) {
          splitIndex = cursorPosition;
        }
        const text_before = text.slice(0, splitIndex);
        const text_after = text.slice(splitIndex);
        
       
        if (shouldSplit(text, splitIndex, enforceRules, true) == false) {
          notify('danger', 'while splitting text \'' + text +'\' at ' + splitIndex + ' before \'' + text_before + '\' | after \'' + text_after + ' is not allowed\'');
          
          return false;
        }
        isInWord = false;
      } else {
        splitIndex = cursorPosition;
      }

      const firstText = text.slice(0, splitIndex).trim();
      const secondText = text.slice(splitIndex).trim();

      // Calculate the split time based on the new split index
      const splitTimeCalculated = entry.startTime + (splitIndex / totalLength) * duration;
      let splitTime = Math.min(Math.max(splitTimeCalculated, entry.startTime), entry.endTime);

      if (splitTime > entry.startTime && splitTime < entry.endTime && firstText && secondText) {
        const originalEntry = { ...entry };
        
        // Handle word splitting
        let firstWords: WordEntry[] | undefined;
        let secondWords: WordEntry[] | undefined;
        
        if (entry.words && isInWord) {
          const splitWord = entry.words[splitWordIndex];
          const wordText = splitWord.word;
          const wordPosition = text.indexOf(wordText);
          const relativePosition = splitIndex - wordPosition;
          
          // Calculate the split time within the word
          const wordDuration = splitWord.end - splitWord.start;
          const wordSplitTime = splitWord.start + (relativePosition / wordText.length) * wordDuration;
          
          // Create new word entries for the split word
          const firstPart: WordEntry = {
            word: wordText.slice(0, relativePosition),
            start: splitWord.start,
            end: wordSplitTime
          };
          
          const secondPart: WordEntry = {
            word: wordText.slice(relativePosition),
            start: wordSplitTime,
            end: splitWord.end
          };
          
          // Combine with other words
          firstWords = [
            ...entry.words.slice(0, splitWordIndex),
            firstPart
          ];
          
          secondWords = [
            secondPart,
            ...entry.words.slice(splitWordIndex + 1)
          ];

        } else if (entry.words) {
          // If not splitting a word, just divide the words array
          const firstWordCount = firstText.split(/\s+/).filter(Boolean).length;
         
          firstWords = entry.words.slice(0, firstWordCount);
          secondWords = entry.words.slice(firstWordCount);
        }

        if (firstWords && firstWords.length>0)
          {
            // calculate split time based on words
            splitTime = firstWords[firstWords.length - 1].end;
          }
        
        const newEntry: SrtEntry = {
          id: `${entry.id}_2s_${split_index++}`,
          startTime: splitTime,
          endTime: entry.endTime,
          text: secondText,
          lines: secondText.split('\n').length,
          words: secondWords
        };
        
        // Modify original entry
        entry.id = `${entry.id}_1s_${split_index++}`;
        entry.endTime = splitTime;
        entry.text = firstText;
        entry.lines = firstText.split('\n').length;
        entry.words = firstWords;
        
        if (entry.startTime === undefined || entry.endTime === undefined) {
          console.error('Invalid split entry:', entry);
        }
        if (newEntry.startTime === undefined || newEntry.endTime === undefined) {
          console.error('Invalid split entry:', newEntry);
        }
        console.debug('splitting First:', entry, ' Second: ', newEntry);
        // Add to history
        await this.addToHistory(this.createHistoryChange(
          'split',
          [originalEntry],
          [{ ...entry }, newEntry]
        ));
        
        if (insertIndex === -1) {
          this.srtData.push(newEntry);
        } else {
          this.srtData.splice(insertIndex + 1, 0, newEntry);
        }
        
        this.interfaceNeedsRefresh++;
        notifyChanges();
        
        // Validate timings after split
        validateAllTimings(this.srtData);
      }
    },

    async mergeEntries(ids: string[]) {
      const entries = this.srtData.filter(entry => ids.includes(entry.id));
      if (entries.length < 2) return;
    
      const originalEntries = entries.map(entry => ({ ...entry }));
      
      // Sort entries by start time to merge them in chronological order
      entries.sort((a, b) => a.startTime - b.startTime);
    
      // Merge words if they exist - no timing adjustment needed as words already have absolute timestamps
      let mergedWords: { word: string; start: number; end: number }[] = [];
      for (const entry of entries) {
        if (entry.words) {
          // Words already have correct absolute timestamps, just concatenate them
          mergedWords = [...mergedWords, ...entry.words];
        }
      }
      
      // Sort merged words by start time to ensure proper order
      mergedWords.sort((a, b) => a.start - b.start);
      
      const mergedEntry: SrtEntry = {
        id: `${entries[0].id}_m_${split_index++}`,
        startTime: entries[0].startTime,
        endTime: entries[entries.length - 1].endTime,
        text: entries.map(e => e.text).join(' '), // Merge the text
        lines: entries.reduce((sum, e) => sum + e.lines, 0),
        words: mergedWords.length > 0 ? mergedWords : undefined, // Set the merged words if any exist
      };

      // Add to history before modifying state
      await this.addToHistory(this.createHistoryChange(
        'merge',
        originalEntries,
        [mergedEntry]
      ));
    
      // Find the index of the first entry to be merged
      const firstEntryIndex = this.srtData.findIndex(entry => entry.id === entries[0].id);
    
      // Remove all entries being merged
      this.srtData = this.srtData.filter(entry => !ids.includes(entry.id));
      this.interfaceNeedsRefresh++;
      notifyChanges();
    
      // Insert merged entry at the position of the first original entry
      this.srtData.splice(firstEntryIndex, 0, mergedEntry);
      this.selectedEntries = [mergedEntry.id];
    },

    async setMaxCPL(maxCPL: number) {
      if (this.maxCPL !== maxCPL) { this.maxCPL = maxCPL; notifyChanges(); }
    },
    async setMaxCPS(maxCPS: number) {
      if (this.maxCPS !== maxCPS) { this.maxCPS = maxCPS; notifyChanges(); }
    },
    async setMaxLines(maxLines: number) {
      if (this.maxLines !== maxLines) { this.maxLines = maxLines; notifyChanges(); }
    },

    async setAutoSave(autoSave: boolean, autoSaveInterval: number) {
      if (this.autoSave !== autoSave || this.autoSaveInterval !== autoSaveInterval) {
        this.autoSave = autoSave; this.autoSaveInterval = autoSaveInterval; notifyChanges();
      }
    },
    // Adopt a newly loaded document. Runs inside the serialized history chain:
    // an older queued flush can neither interleave this reset nor resurrect
    // the previous document's history afterwards, and a late old-source edit
    // is dropped by its source tag instead of bleeding into the new document.
    //
    // The single $patch at the end is the one atomic adoption boundary.
    // Nothing before it destroys anything: a pre-save snapshot of the
    // outgoing document (source, rows, unsaved queue, generation, revision)
    // is taken first, and any failure below restores queue and generation
    // verbatim, so the old visible document AND its dirty history survive a
    // quota/IndexedDB failure. A modification that lands after the snapshot
    // (an applied edit, or a still-debounced keystroke) aborts the adoption
    // with an explicit TranscriptAdoptionError('conflict') — the outgoing
    // document and its edits stay visible — instead of being silently
    // replaced by the final patch. Callers (loadSrtData) must catch and
    // settle/retry; see the wave report for the exact contract.
    async replaceTranscript(sourceId: string, entries: SrtEntry[], settings: { maxCPL: number; maxCPS: number; maxLines: number; autoSave: boolean; autoSaveInterval: number }) {
      await flushPendingSubtitleUpdates(this);
      const list = this.toPlainObject(entries) as SrtEntry[];
      if (!sourceId || !Array.isArray(list)) throw new TranscriptAdoptionError('invalid', 'Invalid transcript');
      const previous = flushChains.get(this) ?? Promise.resolve();
      const run = previous.then(async () => {
        if (this.batchTimeoutId) { clearTimeout(this.batchTimeoutId); this.batchTimeoutId = null; }
        const outgoing = {
          sourceId: this.sourceId,
          srtData: this.srtData,
          pending: [...this.pendingChanges],
          generation: generationOf(this),
          revision: revisionOf(this),
        };
        // Fence the outgoing generation BEFORE the first await: a flush parked
        // inside a slow initDB must wake to a bumped generation and drop its
        // snapshot — otherwise its commit can land after this replacement's
        // clear() and resurrect old-source rows into the new history. The
        // fence (and any detached queue) is restored verbatim on failure.
        historyGenerations.set(this, outgoing.generation + 1);
        try {
          const db = await this.initDB();
          if (
            this.sourceId !== outgoing.sourceId ||
            revisionOf(this) !== outgoing.revision ||
            hasPendingSubtitleUpdates(this)
          ) {
            throw new TranscriptAdoptionError('conflict', 'Transcript was edited while the new source was loading; the current document and its edits were kept');
          }
          // Detach the unsaved queue now that the fence is in place: no
          // in-flight flush can write old-source rows over the new document.
          this.pendingChanges = [];
          const history = this.toPlainObject(this.createHistoryChange('bulk', list, list));
          // The commit window cannot re-validate, so writers are rejected at
          // the boundary for its duration (updateEntry -> false, addToHistory
          // -> conflict) instead of being applied-then-wiped by the $patch.
          adoptionLocks.set(this, true);
          await new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(STORE_NAME, 'readwrite');
            transaction.oncomplete = () => resolve();
            transaction.onabort = () => reject(transaction.error || new Error('Transcript load aborted'));
            transaction.onerror = () => reject(transaction.error || new Error('Transcript history unavailable'));
            const store = transaction.objectStore(STORE_NAME);
            const abort = () => {
              try { transaction.abort(); } catch { /* onabort reports the failure */ }
            };
            const clearRequest = store.clear();
            clearRequest.onerror = abort;
            const addRequest = store.add(history);
            addRequest.onerror = abort;
          });
        } catch (error) {
          adoptionLocks.set(this, false);
          historyGenerations.set(this, outgoing.generation);
          // Rebuild the queue from the verbatim copy: entries a fenced flush
          // dropped while this ran are restored, and anything queued during
          // the attempt survives appended (identity-deduped).
          this.pendingChanges = outgoing.pending.concat(
            this.pendingChanges.filter((change) => !outgoing.pending.includes(change)),
          );
          if (error instanceof TranscriptAdoptionError) throw error;
          throw new TranscriptAdoptionError('unavailable', 'Transcript history unavailable; the previous document and its unsaved history were kept');
        }
        adoptionLocks.set(this, false);
        this.$patch({ sourceId, srtData: list, ...settings, historyIndex: 0, totalHistoryEntries: 1, lastKnownDbSize: 1,
          pendingChanges: [], batchTimeoutId: null, selectedEntries: [], selectedEntriesisAutomatic: false, hasSeenAll: false,
          srtDuration: list.reduce((duration, item) => Math.max(duration, item.endTime), 1),
          interfaceNeedsRefresh: this.interfaceNeedsRefresh + 1 });
        notifyChanges();
      });
      flushChains.set(this, run.catch(() => undefined));
      this.pendingFlush = run;
      try {
        await run;
      } finally {
        if (this.pendingFlush === run) this.pendingFlush = null;
      }
    },

    async setSrtData(srtData: SrtEntry[]) {
      // Loaders (notably the API one) may resolve null/undefined when a
      // transcript has no subtitle payload yet — never store a non-array,
      // every consumer reads `.length`/`.map` unconditionally.
      const list = Array.isArray(srtData) ? srtData : [];
      await this.addToHistory(this.createHistoryChange(
        'bulk',
        list,
        list
      ));

      this.srtData = list;
      this.hasSeenAll = false;
      this.srtDuration = this.srtData.reduce((acc, item) => Math.max(acc, item.endTime), 1);
      this.interfaceNeedsRefresh++;
      notifyChanges();
    },

    async addEntry(entryOrEntries: SrtEntry | SrtEntry[], position?: number): Promise<boolean> {
        // Handle array of entries
        if (Array.isArray(entryOrEntries)) {
            const newEntries = entryOrEntries;
            
            // Create history change for batch addition
            await this.addToHistory(this.createHistoryChange(
                'add',
                [], // No entries before
                newEntries // All new entries after
            ));

            // Sort entries by start time if needed
            const sortedEntries = [...newEntries].sort((a, b) => a.startTime - b.startTime);
            
            // Add all entries
            this.srtData.push(...sortedEntries);
            
            // Update duration if needed (loop, not spread: large imports must
            // not blow the call stack).
            let maxEndTime = this.srtDuration;
            for (const entry of sortedEntries) {
                if (entry.endTime > maxEndTime) maxEndTime = entry.endTime;
            }
            this.srtDuration = maxEndTime;
            
            return false;
        }
        else
        {
          // Handle single entry
          const newEntry = entryOrEntries;

          // Validate entry
          if (!this.validateEntry(newEntry)) {
              notify('danger', 'Invalid subtitle entry');
              return false;
          }

          // Create history change
          await this.addToHistory(this.createHistoryChange(
              'add',
              [], // No entries before
              [newEntry] // Single new entry after
          ));

          // If position is specified, insert at that position
          if (typeof position === 'number' && position >= 0 && position <= this.srtData.length) {
              this.srtData.splice(position, 0, newEntry);
          } else {
              // Otherwise add according to start time
              const insertIndex = this.srtData.findIndex(entry => entry.startTime > newEntry.startTime);
              if (insertIndex !== -1) {
                  this.srtData.splice(insertIndex, 0, newEntry);
              } else {
                  // If no entry was found, add to the end
                  this.srtData.push(newEntry);
              }
          }
          this.interfaceNeedsRefresh++;
          notifyChanges();
          return true;
        }

        
    },

    // Helper method to validate entries.
    // quiet=true (batch paths) collects reason strings in quietValidationReasons
    // instead of notifying, so callers can emit a single aggregated toast.
    validateEntry(entry: SrtEntry, quiet = false): boolean {
        const report = (reason: string) => {
          if (quiet) {
            quietValidationReasons.push(reason);
          } else {
            notify('danger', reason);
          }
        };

        // Check required fields
        if (!entry.id || typeof entry.startTime !== 'number' || typeof entry.endTime !== 'number') {
          report('Invalid timing type');
          return false;
        }

        // Check timing
        if (entry.startTime >= entry.endTime || entry.startTime < 0) {
          report('Invalid timing');
          return false;
        }

        // Check for timing conflicts with existing entries
        const hasConflict = this.srtData.some(existing => {
            // Skip if it's the same entry (for updates)
            if (existing.id === entry.id) return false;
            // Check for overlap
            return (entry.startTime < existing.endTime && entry.endTime > existing.startTime);
        });

        if (hasConflict) {
            // One notification per validate call, not per conflicting entry
            report('Conflict with existing entry');
            return false;
        }

        // Check text content
        if (!entry.text || entry.text.trim().length === 0) {
            return false;
        }

        // Calculate lines if not provided
        if (typeof entry.lines !== 'number') {
            entry.lines = entry.text.split('\n').length;
        }

        // Validate lines count
        if (entry.lines <= 0) {
            return false;
        }

        return true;
    },

    // Helper method to assign sequential IDs
    generateEntryId(): string {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `srt_${timestamp}_${random}`;
    },

    // Method to add multiple entries efficiently
    async addEntries(entries: SrtEntry[]): Promise<void> {
        // Validate all entries first (quiet: no per-entry toasts)
        quietValidationReasons = [];
        const validEntries = entries.filter(entry => this.validateEntry(entry, true));
        const invalidCount = entries.length - validEntries.length;
        if (invalidCount > 0) {
            // Single aggregated toast for the whole batch
            notify('danger', getTranslation('{n} entries had invalid timings and were skipped', { n: invalidCount }));
        }

        if (validEntries.length === 0) {
            throw new Error('No valid entries to add');
        }

        // Sort entries by start time
        const sortedEntries = validEntries.sort((a, b) => a.startTime - b.startTime);

        // Add them all at once
        await this.addEntry(sortedEntries);
    },

    // Method to add entry at specific time
    async addEntryAtTime(text: string, startTime: number, duration: number): Promise<void> {
        const newEntry: SrtEntry = {
            id: this.generateEntryId(),
            startTime,
            endTime: startTime + duration,
            text,
            lines: text.split('\n').length,
        };

        await this.addEntry(newEntry);
    },

    // Method to add entry after another entry
    async addEntryAfter(text: string, afterId: string, gap = 0): Promise<void> {
        const afterEntry = this.srtData.find(entry => entry.id === afterId);
        if (!afterEntry) {
            throw new Error('Reference entry not found');
        }

        const newEntry: SrtEntry = {
            id: this.generateEntryId(),
            startTime: afterEntry.endTime + gap,
            endTime: afterEntry.endTime + gap + (afterEntry.endTime - afterEntry.startTime), // Same duration as previous
            text,
            lines: text.split('\n').length,
        };

        await this.addEntry(newEntry);
    },

    // Method to add entry before another entry
    async addEntryBefore(text: string, beforeId: string, gap = 0): Promise<void> {
        const beforeEntry = this.srtData.find(entry => entry.id === beforeId);
        if (!beforeEntry) {
            throw new Error('Reference entry not found');
        }

        const newEntry: SrtEntry = {
            id: this.generateEntryId(),
            endTime: beforeEntry.startTime - gap,
            startTime: beforeEntry.startTime - gap - (beforeEntry.endTime - beforeEntry.startTime), // Same duration as next
            text,
            lines: text.split('\n').length,
        };

        if (newEntry.startTime < 0) {
            throw new Error('Cannot add entry before 0 timestamp');
        }

        await this.addEntry(newEntry);
    },

    // Applies one cue update. Queued-edit callers pass the source they read the
    // cue from; when a source replacement landed first, the edit is dropped
    // (returns false) instead of writing stale rows into the new document.
    // Direct UI callers omit the guard and keep the previous behavior.
    async updateEntry(updatedEntry: SrtEntry, beforeEntry?: SrtEntry, expectedSourceId?: string | null): Promise<boolean> {
      // A replacement/clear commit owns the document right now: the write
      // would be wiped by the adoption patch, so drop it explicitly (false)
      // instead of recording history that throws or vanishing silently. The
      // queued-edit layer resolves such waiters false; direct callers retry.
      if (isAdopting(this)) return false;
      if (expectedSourceId != null && expectedSourceId !== this.sourceId) return false;
      const index = this.srtData.findIndex(entry => entry.id === updatedEntry.id);
      if (index !== -1) {
        // Callers that mutate the live entry in place before calling (e.g. timeline
        // drags) must pass the pre-mutation snapshot — snapshotting srtData[index]
        // after the fact would make before === after and undo a no-op.
        const originalEntry = beforeEntry ? { ...beforeEntry } : { ...this.srtData[index] };
        const queuedChange = await this.addToHistory(this.createHistoryChange(
          'update',
          [originalEntry],
          [updatedEntry]
        ));
        // Re-check after the history hop: a source replacement may have
        // landed while this update was queued behind other history work.
        // Retract our own history record too — it describes the previous
        // document and must not be batched into the new source's history.
        if (expectedSourceId != null && expectedSourceId !== this.sourceId) {
          const at = this.pendingChanges.indexOf(queuedChange);
          if (at !== -1) this.pendingChanges.splice(at, 1);
          return false;
        }

        this.srtData.splice(index, 1, updatedEntry);
        this.interfaceNeedsRefresh++;
        notifyChanges();
        return true;
      }
      return false;
    },

    async deleteEntry(id: string) {
      await flushPendingSubtitleUpdates(this);
      const entry = this.srtData.find(entry => entry.id === id);
      if (entry) {
        await this.addToHistory(this.createHistoryChange(
          'delete',
          [entry],
          []
        ));
        
        this.srtData = this.srtData.filter(entry => entry.id !== id);
        this.interfaceNeedsRefresh++;
        notifyChanges();
      }
    },

async clearCache() {
  // Runs inside the serialized history chain with a new document generation:
  // an in-flight flush from before the clear can neither interleave the wipe
  // nor resurrect wiped rows afterwards.
  //
  // The wipe is ONE readwrite transaction holding a single store.clear(),
  // acknowledged on tx.oncomplete — never on request success. Chunked
  // multi-transaction deletes were removed deliberately: the history is
  // bounded to MAX_HISTORY_SIZE rows (50), so a single atomic clear is both
  // correct and cheap. If that bound ever grows by orders of magnitude, a
  // chunked design must re-establish atomicity (generation fencing per chunk
  // plus a commit marker), not just split the deletes.
  //
  // On failure NOTHING in memory is reset: generation, unsaved queue, rows,
  // and counters are restored/retained, and the caller observes a
  // TranscriptAdoptionError('unavailable') (after the user-facing toast) so
  // reset/sign-out flows can react instead of assuming a clean slate.
  // Callers: the sign-out route MUST add .catch (it currently ignores the
  // promise); see the wave report.
  const previous = flushChains.get(this) ?? Promise.resolve();
  const run = previous.then(async () => {
    if (this.batchTimeoutId) { clearTimeout(this.batchTimeoutId); this.batchTimeoutId = null; }
    await flushPendingSubtitleUpdates(this);
    const outgoing = {
      pending: this.pendingChanges,
      generation: generationOf(this),
      revision: revisionOf(this),
      sourceId: this.sourceId,
    };
    const db = await this.initDB();
    if (
      this.sourceId !== outgoing.sourceId ||
      revisionOf(this) !== outgoing.revision ||
      hasPendingSubtitleUpdates(this)
    ) {
      throw new TranscriptAdoptionError('conflict', 'Transcript was edited while the cache clear was starting; nothing was cleared');
    }
    historyGenerations.set(this, outgoing.generation + 1);
    this.pendingChanges = [];
    try {
      adoptionLocks.set(this, true);
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error || new Error('Cache clear aborted'));
        tx.onerror = () => reject(tx.error || new Error('Cache clear failed'));
        const store = tx.objectStore(STORE_NAME);
        const clearRequest = store.clear();
        clearRequest.onerror = () => {
          try { tx.abort(); } catch { /* onabort reports the failure */ }
        };
      });
    } catch (error) {
      adoptionLocks.set(this, false);
      historyGenerations.set(this, outgoing.generation);
      this.pendingChanges = outgoing.pending.concat(this.pendingChanges);
      console.error('Error clearing cache:', error);
      const errorMessage = (error instanceof Error) ? error.message : String(error);
      notify('danger', 'Failed to clear cache: ' + errorMessage + ' - please check your available storage space');
      if (error instanceof TranscriptAdoptionError) throw error;
      throw new TranscriptAdoptionError('unavailable', 'Failed to clear cache: ' + errorMessage);
    }
    adoptionLocks.set(this, false);

    // The wipe committed: only now reset the in-memory document.
    this.historyIndex = -1;
    this.totalHistoryEntries = 0;
    this.lastKnownDbSize = 0;
    this.srtData = [];
    this.interfaceNeedsRefresh++;
    this.selectedEntries = [];
    this.selectedEntriesisAutomatic = false;
    this.pendingChanges = [];
    this.hasSeenAll = false;
  });
  flushChains.set(this, run.catch(() => undefined));
  this.pendingFlush = run;
  try {
    await run;
  } finally {
    if (this.pendingFlush === run) this.pendingFlush = null;
  }
  },

  async selectEntry(id: string, multiSelect: boolean, automatic:boolean): Promise<boolean> {    
 
    if (multiSelect) {
      if (this.selectedEntries.includes(id)) {
        // update of selectedEntries not needed
      } else {
        this.selectedEntries.push(id);
        this.selectedEntriesisAutomatic = automatic;
        return true;
      }
      
    } else {
      if (!this.selectedEntries.includes(id))
      {
        this.selectedEntries = [id];
        this.selectedEntriesisAutomatic = automatic;
        return true;
      }
    }
    return false;
  },

  clearSelection() {
    this.selectedEntries = [];
  },

  // Utility method to get selected entries data
  getSelectedEntries(): SrtEntry[] {
    return this.srtData.filter(entry => this.selectedEntries.includes(entry.id));
  },

  // Method to check if entries can be merged
  canMergeEntries(): boolean {
    if (this.selectedEntries.length < 2) return false;
    
    const entries = this.getSelectedEntries();
    // Sort by start time to check continuity
    entries.sort((a, b) => a.startTime - b.startTime);
    
    // Check if entries are continuous
    for (let i = 0; i < entries.length - 1; i++) {
      if (entries[i].endTime !== entries[i + 1].startTime) {
        return false;
      }
    }
    return true;
  },

  // Method to check if entry can be split
  canSplitEntry(id: string): boolean {
    const entry = this.srtData.find(e => e.id === id);
    if (!entry) return false;
    
    // Check if entry has enough duration and content to split
    const minDuration = 0.5; // minimum 500ms
    return (entry.endTime - entry.startTime) >= minDuration && 
           (entry.text?.length ?? 0) > 1;
  },

  // Method to get entry duration
  getEntryDuration(id: string): number {
    const entry = this.srtData.find(e => e.id === id);
    return entry ? entry.endTime - entry.startTime : 0;
  },

  // Method to validate entry timing
  validateEntryTiming(entry: SrtEntry): boolean {
    // Check if entry timing is valid
    if (entry.startTime >= entry.endTime) return false;
    if (entry.startTime < 0) return false;
    
    // Check for overlaps with other entries
    const otherEntries = this.srtData.filter(e => e.id !== entry.id);
    for (const other of otherEntries) {
      if (entry.startTime < other.endTime && entry.endTime > other.startTime) {
        return false;
      }
    }
    return true;
  },

  // Method to find gaps in subtitle timing
  findGaps(): Array<{ start: number; end: number }> {
    if (this.srtData.length < 2) return [];
    
    const gaps: Array<{ start: number; end: number }> = [];
    const sortedEntries = [...this.srtData].sort((a, b) => a.startTime - b.startTime);
    
    for (let i = 0; i < sortedEntries.length - 1; i++) {
      const currentEnd = sortedEntries[i].endTime;
      const nextStart = sortedEntries[i + 1].startTime;
      
      if (nextStart - currentEnd > 0.1) { // Gap larger than 100ms
        gaps.push({
          start: currentEnd,
          end: nextStart
        });
      }
    }
    return gaps;
  },

  // Method to find overlapping entries
  findOverlaps(): Array<{ entry1: SrtEntry; entry2: SrtEntry }> {
    const overlaps: Array<{ entry1: SrtEntry; entry2: SrtEntry }> = [];
    const sortedEntries = [...this.srtData].sort((a, b) => a.startTime - b.startTime);
    
    for (let i = 0; i < sortedEntries.length - 1; i++) {
      const current = sortedEntries[i];
      const next = sortedEntries[i + 1];
      
      if (current.endTime > next.startTime) {
        overlaps.push({
          entry1: current,
          entry2: next
        });
      }
    }
    return overlaps;
  },

  // Method to check if entry exists at a specific time
  getEntryAtTime(time: number): SrtEntry | undefined {
    return this.srtData.find(entry => 
      time >= entry.startTime && time <= entry.endTime
    );
  },

  // Method to get entries within a time range
  getEntriesInRange(start: number, end: number): SrtEntry[] {
    return this.srtData.filter(entry =>
      (entry.startTime >= start && entry.startTime <= end) ||
      (entry.endTime >= start && entry.endTime <= end) ||
      (entry.startTime <= start && entry.endTime >= end)
    );
  },

  // Method to get the total duration of all entries
  getTotalDuration(): number {
    return this.srtDuration;
  },

  // Method to adjust timing of all entries
  async adjustTiming(offset: number) {
    const originalEntries = this.srtData.map(entry => ({ ...entry }));
    const adjustedEntries = this.srtData.map(entry => ({
      ...entry,
      startTime: Math.max(0, entry.startTime + offset),
      endTime: Math.max(0, entry.endTime + offset)
    }));

    await this.addToHistory(this.createHistoryChange(
      'bulk',
      originalEntries,
      adjustedEntries
    ));

    this.srtData = adjustedEntries;
    this.interfaceNeedsRefresh++;
    notifyChanges();
    this.srtDuration = this.srtData.reduce((acc, item) => 
      Math.max(acc, item.endTime), 1
    );
  },

  // Method to format time for display
  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${
      minutes.toString().padStart(2, '0')}:${
      secs.toString().padStart(2, '0')},${
      ms.toString().padStart(3, '0')}`;
  },

  // Method to parse formatted time string
  parseTime(timeString: string): number {
    const [time, ms] = timeString.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    
    return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000;
  },

  // Method to export current state
  getExportData() {
    return {
      srtData: this.srtData,
      version: DB_VERSION,
      timestamp: Date.now(),
      duration: this.srtDuration
    };
  },

  // Method to handle store cleanup
  cleanup() {
    if (this.batchTimeoutId) {
      clearTimeout(this.batchTimeoutId);
      this.batchTimeoutId = null;
    }
    this.pendingChanges = [];
  }
},

// Add getters for common calculations
getters: {
  // Add these to your state interface
  canUndo: (state) => state.historyIndex > 0 , 

  hasSelection: (state) => state.selectedEntries.length > 0,
  
  totalEntries: (state) => state.srtData.length,
  
  totalWords: (state) => state.srtData.reduce((sum, entry) => 
    sum + (entry.text?.split(/\s+/).filter(Boolean).length ?? 0), 0
  ),
  
  averageDuration: (state) => {
    if (state.srtData.length === 0) return 0;
    return state.srtData.reduce((sum, entry) => 
      sum + (entry.endTime - entry.startTime), 0
    ) / state.srtData.length;
  },

  hasGaps: (state) => {
    const sortedEntries = [...state.srtData].sort((a, b) => a.startTime - b.startTime);
    for (let i = 0; i < sortedEntries.length - 1; i++) {
      if (sortedEntries[i + 1].startTime - sortedEntries[i].endTime > 0.1) {
        return true;
      }
    }
    return false;
  },

  hasOverlaps: (state) => {
    const sortedEntries = [...state.srtData].sort((a, b) => a.startTime - b.startTime);
    for (let i = 0; i < sortedEntries.length - 1; i++) {
      if (sortedEntries[i].endTime > sortedEntries[i + 1].startTime) {
        return true;
      }
    }
    return false;
  }
}
});
